import React from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import AnimatedBackground from '@/components/AnimatedBackground';
import {
  CURRENT_PRIVACY_POLICY_VERSION,
  CURRENT_PRIVACY_POLICY_EFFECTIVE_DATE,
} from '@/utils/privacyPolicyVersion';

const PrivacyPolicyPage = () => {
  const h1 = 'text-[#FFDD59] text-4xl font-bold mb-4';
  const h2 = 'text-[#FFDD59] text-2xl font-semibold mt-8 mb-3';
  const p = 'text-gray-300 leading-relaxed mb-4';
  const strong = 'text-white font-semibold';
  const list = 'text-gray-300 leading-relaxed mb-4 list-disc pl-6 space-y-1';

  return (
    <div className="min-h-screen bg-black relative flex flex-col">
      <AnimatedBackground />
      <div className="relative z-10 flex flex-col min-h-screen">
        <Navbar />
        <div className="pt-32 pb-12 bg-black bg-opacity-75 flex-1">
          <div className="max-w-4xl mx-auto px-4 py-12">
            <h1 className={h1}>Privacy Policy</h1>
            <p className={p}>
              <span className={strong}>Version {CURRENT_PRIVACY_POLICY_VERSION}</span> • <span className={strong}>Last Updated:</span> {CURRENT_PRIVACY_POLICY_EFFECTIVE_DATE}
            </p>

            <div className="bg-yellow-900/20 border border-yellow-600/40 rounded p-4 mb-8">
              <p className="text-gray-300 leading-relaxed">
                This Privacy Policy (Version {CURRENT_PRIVACY_POLICY_VERSION}) is designed to comply with the EU General Data
                Protection Regulation (GDPR) and the UAE Personal Data Protection Law (Federal
                Decree-Law No. 45 of 2021). For questions about specific provisions, contact
                info@personagency.com.
              </p>
            </div>

            <h2 className={h2}>1. Introduction</h2>
            <p className={p}>
              PERSONA EVENT AND MARKETING SOLUTIONS FZ LLE ("Persona", "we", "us", "our")
              respects your privacy and is committed to protecting your personal data. This Privacy
              Policy explains how we collect, use, store, and protect personal data when you visit
              personagency.com (the "Website") or interact with our influencer marketing services.
            </p>
            <p className={p}>
              This Policy is designed to comply with the General Data Protection Regulation (GDPR)
              and the UAE Personal Data Protection Law (Federal Decree-Law No. 45 of 2021).
            </p>

            <h2 className={h2}>2. Who We Are</h2>
            <p className={p}>
              Persona is an influencer marketing agency connecting brands with content
              creators across the United Arab Emirates and the GCC region. We specialize in
              mom-influencer partnerships and lifestyle brand collaborations.
            </p>
            <p className={p}>
              <span className={strong}>Data Controller:</span>
              <br />
              PERSONA EVENT AND MARKETING SOLUTIONS FZ LLE
              <br />
              Contact: info@personagency.com
            </p>
            <p className={p}>
              Persona is registered in Fujairah Creative City, United Arab Emirates,
              operating under the regulatory framework of UAE Federal Decree-Law No. 45 of
              2021 (PDPL).
            </p>

            <h2 className={h2}>3. What Data We Collect</h2>
            <p className={p}>
              <span className={strong}>3.1 Influencer Application Data</span>
              <br />
              When you submit our influencer registration form: full name, email, phone with
              country code, date of birth or birth year, country/region, family information
              (children count, birth dates, expected due dates), influencer category, social media
              profiles (usernames, follower counts, view metrics, pricing expectations), content
              categories, budget expectations.
            </p>
            <p className={p}>
              <span className={strong}>3.2 Brand Contact Data</span>
              <br />
              When you submit a brand inquiry: name, email, company name, message content.
            </p>
            <p className={p}>
              <span className={strong}>3.3 Technical Data</span>
              <br />
              IP address, browser type, device information, pages visited.
            </p>
            <p className={p}>
              <span className={strong}>3.4 Communications Data</span>
              <br />
              Records of correspondence between you and Persona.
            </p>
            <p className={p}>
              <span className={strong}>3.5 Special Categories of Data (Sensitive Personal Data)</span>
            </p>
            <p className={p}>
              We collect data classified as 'sensitive personal data' under UAE PDPL Article 1 and
              GDPR Article 9, specifically:
            </p>
            <ul className={list}>
              <li>
                <span className={strong}>Health-related information:</span> pregnancy status and
                expected due dates
              </li>
              <li>
                <span className={strong}>Family composition:</span> number and birth dates of
                children
              </li>
            </ul>
            <p className={p}>
              This data is processed only with your <span className={strong}>explicit consent</span>{' '}
              (UAE PDPL Article 5; GDPR Article 9(2)(a)) and used solely for brand-influencer
              matching purposes.
            </p>

            <h2 className={h2}>4. How We Use Your Data</h2>
            <ul className={list}>
              <li>Service Delivery: Matching influencers with brand campaigns</li>
              <li>Communication: Responding to inquiries, sending opportunities</li>
              <li>Database Maintenance: Influencer network for ongoing partnerships</li>
              <li>Analytics: Improving services and Website performance</li>
              <li>Legal Compliance: Meeting legal obligations</li>
            </ul>

            <h2 className={h2}>5. Legal Basis for Processing (GDPR)</h2>
            <ul className={list}>
              <li>
                <span className={strong}>Consent:</span> Form submissions constitute explicit
                consent
              </li>
              <li>
                <span className={strong}>Legitimate Interest:</span> Operating and improving our
                services
              </li>
              <li>
                <span className={strong}>Contractual Necessity:</span> Performing agreed services
              </li>
              <li>
                <span className={strong}>Legal Obligation:</span> Complying with applicable laws
              </li>
            </ul>
            <p className={p}>
              <span className={strong}>For sensitive personal data (see Section 3.5),</span> our
              legal basis is your <span className={strong}>explicit consent</span> under:
            </p>
            <ul className={list}>
              <li>UAE PDPL Article 5 (lawful processing) and Article 6 (consent requirements)</li>
              <li>GDPR Article 9(2)(a) (explicit consent for special categories of personal data)</li>
            </ul>
            <p className={p}>You provide this consent through the second checkbox on our application form.</p>

            <h2 className={h2}>6. Data Sharing</h2>
            <p className={p}>
              <span className={strong}>6.1 Brand Partners</span>
              <br />
              Influencer profile data may be shared with brands considering partnerships, with your
              prior knowledge.
            </p>
            <p className={p}>
              <span className={strong}>6.2 Service Providers</span>
            </p>
            <ul className={list}>
              <li>Email service: Mailgun</li>
              <li>Database hosting: MongoDB Atlas</li>
              <li>Cloud infrastructure: Google Cloud Platform (Belgium, EU region)</li>
            </ul>
            <p className={p}>All bound by data processing agreements.</p>
            <p className={p}>
              <span className={strong}>6.3 Legal Requirements</span>
              <br />
              We may disclose data if required by law, court order, or governmental authority.
            </p>
            <p className={p}>
              <span className={strong}>We do not sell your personal data.</span>
            </p>

            <h2 className={h2}>7. International Data Transfers</h2>
            <p className={p}>
              Personal data may be transferred outside the UAE to countries that provide an
              adequate level of protection (as determined by the UAE Data Office). While we
              utilize EU-based cloud servers (Google Cloud Platform, europe-west1 region), we
              comply with UAE cross-border transfer regulations under{' '}
              <span className={strong}>Articles 22 &amp; 23 of Federal Decree-Law No. 45 of 2021</span>.
              In the absence of an adequacy decision, we rely on Standard Contractual Clauses
              (SCCs) embedded in our Data Processing Agreements with service providers.
            </p>

            <h2 className={h2}>8. Data Retention</h2>
            <p className={p}>
              We retain personal data for 3 years from the date of collection, unless a longer
              period is required for legal compliance, regulatory obligations, or ongoing
              contractual relationships.
            </p>
            <p className={p}>
              After the retention period: secure deletion or anonymization. Earlier deletion
              requests may be sent to info@personagency.com.
            </p>

            <h2 className={h2}>9. Your Rights</h2>
            <p className={p}>
              Under both <span className={strong}>UAE PDPL</span> (Federal Decree-Law No. 45 of
              2021, Articles 13–17) and <span className={strong}>GDPR</span> (Articles 15–22), you
              have the following rights:
            </p>
            <ul className={list}>
              <li>Right of Access</li>
              <li>Right to Rectification</li>
              <li>Right to Erasure</li>
              <li>Right to Restrict Processing</li>
              <li>Right to Data Portability</li>
              <li>Right to Object</li>
              <li>Right to Withdraw Consent</li>
              <li>Right to Lodge a Complaint with supervisory authority</li>
            </ul>
            <p className={p}>
              <span className={strong}>9.1 Automated Decision-Making</span>
              <br />
              We use our proprietary analytics engine to categorize influencers based on social
              media metrics. While this process involves automated analysis, it does not
              produce legal effects on you. You have the right to request a human review of any
              automated decisions by contacting our Data Protection Officer at info@personagency.com.
            </p>
            <p className={p}>
              <span className={strong}>9.2 How to Exercise Your Rights</span>
              <br />
              To exercise any of the rights listed above (access, deletion, correction,
              objection, portability), please contact us at{' '}
              <span className={strong}>info@personagency.com</span> with your request. We will
              respond within 30 days as required by UAE PDPL and GDPR. For verification
              purposes, we may request additional information to confirm your identity before
              processing your request.
            </p>
            <p className={p}>
              <span className={strong}>9.3 Right to Anonymization</span>
              <br />
              You have the right to request that your personal data be rendered permanently and
              irreversibly anonymous such that it can no longer be used to identify you.
              Anonymized data may be retained for statistical, research, or business analytics
              purposes in accordance with applicable laws.
            </p>
            <p className={p}>
              If you believe your rights have been violated, you may file a complaint with the{' '}
              <span className={strong}>UAE Data Office</span>, the designated supervisory
              authority under Federal Decree-Law No. 45 of 2021. For users in the EU, complaints
              may also be filed with the relevant national data protection authority.
            </p>

            <h2 className={h2}>10. Cookies</h2>
            <p className={p}>
              Essential cookies for functionality. Analytics cookies (anonymized). Detailed Cookie
              Policy separately. Manage via browser settings.
            </p>

            <h2 className={h2}>11. Children's Privacy</h2>
            <p className={p}>
              Services intended for 18+. We do not knowingly collect data from minors.
            </p>
            <p className={p}>
              "Mom" and "Mom-to-be" categories: adult women only. Children's information (birth
              dates) collected from consenting parent only for campaign matching.
            </p>
            <p className={p}>
              Inadvertent collection from minor → contact info@personagency.com immediately.
            </p>
            <p className={p}>
              By providing children's information, the parent or legal guardian provides
              explicit consent for such processing for campaign-matching purposes only. This
              data is retained until the parent withdraws consent or the child reaches the age
              of majority, whichever occurs first. We do not share children's data with third
              parties without separate explicit consent.
            </p>

            <h2 className={h2}>12. Data Security</h2>
            <ul className={list}>
              <li>TLS/HTTPS encryption in transit</li>
              <li>Encrypted database storage</li>
              <li>Access controls and authentication</li>
              <li>Regular security reviews</li>
              <li>Limited employee access on need-to-know basis</li>
            </ul>
            <p className={p}>
              No method 100% secure; we strive but cannot guarantee absolute security.
            </p>
            <p className={p}>
              <span className={strong}>Breach Notification.</span> In the event of a personal
              data breach, Persona will immediately notify the UAE Data Office and,
              where required, the affected individuals within the timelines specified by
              Federal Decree-Law No. 45 of 2021 (typically 72 hours for GDPR-relevant cases).
            </p>

            <h2 className={h2}>13. Updates to This Privacy Policy</h2>
            <p className={p}>
              Updates from time to time. "Last Updated" indicates revision. Material changes
              notified via Website or email.
            </p>

            <h2 className={h2}>14. Contact Us</h2>
            <p className={p}>
              <span className={strong}>PERSONA EVENT AND MARKETING SOLUTIONS FZ LLE</span>
              <br />
              <span className={strong}>Email:</span> info@personagency.com
              <br />
              <span className={strong}>Website:</span> https://personagency.com
            </p>
            <p className={p}>
              <span className={strong}>Data Protection Officer</span>
              <br />
              For data protection inquiries, including the exercise of your rights, you may
              contact our Data Protection Officer at info@personagency.com.
            </p>
            <p className={p}>
              Under UAE PDPL, contact UAE Data Office if rights violated.
            </p>

            <h2 className={h2}>15. Policy Versioning and Changes</h2>
            <p className={p}>
              <span className={strong}>Current Version:</span> {CURRENT_PRIVACY_POLICY_VERSION}
              <br />
              <span className={strong}>Effective Date:</span> {CURRENT_PRIVACY_POLICY_EFFECTIVE_DATE}
            </p>
            <p className={p}>
              We maintain a versioning system for this Privacy Policy to ensure transparency about
              updates:
            </p>
            <ul className={list}>
              <li>
                <span className={strong}>Major version increments (v2.0, v3.0...):</span> Material
                changes such as new processing purposes, new third-party recipients, or changes
                affecting your rights
              </li>
              <li>
                <span className={strong}>Minor version increments (v1.1, v1.2...):</span>{' '}
                Clarifications, formatting updates, or non-material adjustments
              </li>
            </ul>
            <p className={p}>When you submit our forms, we record:</p>
            <ul className={list}>
              <li>The version of this Privacy Policy active at the time of your consent</li>
              <li>A timestamp of your consent</li>
              <li>Your IP address and user agent (browser/device information)</li>
            </ul>
            <p className={p}>
              This audit record demonstrates compliance with{' '}
              <span className={strong}>UAE PDPL Article 6</span> (consent proof) and{' '}
              <span className={strong}>GDPR Article 7(1)</span> (demonstration of consent), and
              ensures that your consent was specific to the Privacy Policy version active at
              submission.
            </p>
            <p className={p}>
              Previous versions of this Policy are available upon request via{' '}
              <span className={strong}>info@personagency.com</span>.
            </p>

            {/* Compliance Seal */}
            <div
              className="mt-12 mb-8 bg-gray-900 border border-gray-800 rounded-lg p-6 text-center"
              data-testid="compliance-seal"
            >
              <div className="flex items-center justify-center gap-2 mb-3">
                <span className="text-2xl" aria-hidden="true">🔒</span>
                <h3 className="text-lg font-semibold text-white">Global Privacy Verified</h3>
              </div>
              <p className="text-sm text-gray-400 leading-relaxed mb-2">
                Persona strictly adheres to international data protection standards,
                including{' '}
                <span className="text-[#FFDD59] font-medium">
                  UAE Federal Decree-Law No. 45 of 2021 (PDPL)
                </span>{' '}
                and{' '}
                <span className="text-[#FFDD59] font-medium">EU GDPR</span>. Registered and
                compliant within{' '}
                <span className="text-[#FFDD59] font-medium">Fujairah Creative City, UAE</span>.
              </p>
              <p className="text-xs text-gray-500 italic">
                Privacy Version: {CURRENT_PRIVACY_POLICY_VERSION} | 2026
              </p>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
